import { memo, useEffect, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, BoxProps, Heading, HStack, Skeleton, VStack } from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';

import { useCheckInsOverTime } from '../../hooks/use-check-ins-over-time';
import { MoodChart } from './mood-chart';
import { RecentCheckInsList } from './recent-check-ins-list';
import { TimePeriodSelector } from './time-period-selector';
import { TimePeriod } from './types';

interface CheckInsOverTimeWidgetProps extends BoxProps {
	meetingId: string;
	guestId: string;
	hostId: string;
}

function CheckInsOverTimeWidgetComponent({ meetingId, guestId, hostId, ...props }: CheckInsOverTimeWidgetProps) {
	const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('12w');
	const [selectedMonth, setSelectedMonth] = useState<string | undefined>();

	const { data, isLoading, error } = useCheckInsOverTime(meetingId, selectedPeriod);
	const { data: hostUser } = useUser(hostId);
	const { data: guestUser } = useUser(guestId);
	const { track } = useAnalytics();

	// Track widget viewed when data loads
	useEffect(() => {
		if (data && !isLoading) {
			track(AnalyticsEvent.CheckIns.OverTimeWidgetViewed, {
				meetingId,
				period: selectedPeriod,
				checkInsCount: data.length,
			});
		}
	}, [data, isLoading, meetingId, selectedPeriod, track]);

	const handlePeriodChange = (period: TimePeriod) => {
		setSelectedPeriod(period);
		setSelectedMonth(undefined); // Clear month filter when changing period
	};

	const handleColumnClick = (month: string) => {
		setSelectedMonth(selectedMonth === month ? undefined : month);
	};

	const handleMonthFilterRemove = () => {
		setSelectedMonth(undefined);
	};

	const guestName = guestUser?.displayFirstName === hostUser?.displayFirstName ? guestUser?.displayName : guestUser?.displayFirstName;

	if (error) {
		return (
			<Box p={6} bg="bg.surface" borderRadius="lg" boxShadow="lg" maxW="2xl" w="full">
				<Alert status="error">
					<AlertIcon />
					<Box>
						<AlertTitle>Error loading check-ins: </AlertTitle>
						<AlertDescription>Unable to load check-ins data. Please try again later.</AlertDescription>
					</Box>
				</Alert>
			</Box>
		);
	}

	return (
		<VStack align="stretch" data-component="one-on-one-drawer-check-ins-over-time" spacing={4} {...props}>
			<HStack spacing={3} align="center" justify="space-between">
				<Heading fontSize="lg">{guestName}'s history</Heading>
				<TimePeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} />
			</HStack>

			{/* Chart Section */}
			<Box>
				{isLoading ? (
					<VStack spacing={4} align="stretch">
						<Skeleton height="200px" borderRadius="md" />
						<HStack justify="center" spacing={4}>
							{[1, 2, 3, 4, 5, 6].map((i) => (
								<Skeleton key={i} height="20px" width="40px" />
							))}
						</HStack>
					</VStack>
				) : (
					data && <MoodChart selectedPeriod={selectedPeriod} checkIns={data} onColumnClick={handleColumnClick} selectedMonth={selectedMonth} />
				)}
			</Box>

			{/* Recent Check-ins Section */}
			<Box>
				{isLoading ? (
					<VStack spacing={4} align="stretch">
						<Skeleton height="24px" width="200px" />
						{[1, 2, 3].map((i) => (
							<Box key={i} p={3} borderWidth="1px" borderRadius="md">
								<HStack spacing={3}>
									<Skeleton height="32px" width="32px" borderRadius="full" />
									<VStack align="start" spacing={2} flex={1}>
										<HStack justify="space-between" w="100%">
											<Skeleton height="16px" width="40px" />
											<Skeleton height="16px" width="80px" />
										</HStack>
										<Skeleton height="40px" width="100%" />
									</VStack>
								</HStack>
							</Box>
						))}
					</VStack>
				) : (
					data && <RecentCheckInsList checkIns={data} selectedMonth={selectedMonth} onMonthFilterRemove={handleMonthFilterRemove} insights={undefined} />
				)}
			</Box>
		</VStack>
	);
}

// avoid re-rendering when the props change, we could remove memo once drawer code is refactored
export const CheckInsOverTimeWidget = memo(CheckInsOverTimeWidgetComponent);
